<template>
  <woot-modal :show="show" :on-close="handleClose" :header-title="$t('IMAGE_LIBRARY.UPLOAD_IMAGES')">
    <!-- Body: giới hạn chiều cao, bật scroll để không bị cắt modal -->
    <div class="space-y-4 max-h-[65vh] overflow-y-auto overflow-x-hidden pr-1 border border-gray-200 rounded-lg p-4 bg-white">
      <!-- Dropzone -->
      <div
        class="rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 p-6 text-center cursor-pointer hover:bg-gray-100"
        @click="openPicker"
        @dragover.prevent
        @drop.prevent="onDrop"
      >
        <fluent-icon icon="arrow-upload" size="24" class="text-indigo-600 mb-2" />
        <div class="text-sm text-gray-700">{{ $t('IMAGE_LIBRARY.UPLOAD_DROP') }}</div>
        <div class="text-xs text-gray-500 mt-1">{{ $t('IMAGE_LIBRARY.UPLOAD_ACCEPT') }}</div>
        <div class="text-xs text-purple-600 mt-2 font-medium">{{ $t('IMAGE_LIBRARY.UPLOAD_TIP') }}</div>
        <input ref="fileInput" type="file" class="hidden" multiple accept="image/*" @change="onFiles" />
      </div>

      

      <!-- Preview list -->
      <div v-if="files.length" class="space-y-2">
        <div class="text-sm text-gray-600">{{ files.length }} {{ $t('IMAGE_LIBRARY.IMAGES_SELECTED') }}</div>
        <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          <div v-for="(f, idx) in files" :key="idx" class="border border-gray-200 rounded-md p-2 bg-white">
            <div class="aspect-square rounded overflow-hidden bg-gray-100 mb-2 cursor-pointer" @click="openEdit(idx)">
              <img :src="f.url" class="w-full h-full object-cover" @error="onImgError(idx)" />
            </div>
            <div class="text-xs text-gray-700 truncate">{{ f.title || f.file.name }}</div>
            <div class="text-[11px] text-gray-500">{{ humanSize(f.file.size) }}</div>
            <div class="mt-1 flex justify-end gap-1">
              <woot-button size="small" variant="clear" :title="$t('IMAGE_LIBRARY.ACTIONS.EDIT')" @click="openEdit(idx)">
                <fluent-icon icon="edit" size="16" />
              </woot-button>
              <woot-button size="small" variant="clear" :title="$t('IMAGE_LIBRARY.ACTIONS.DELETE')" @click="removeAt(idx)">
                <fluent-icon icon="delete" size="16" class="text-red-600 hover:text-red-700" />
              </woot-button>
            </div>
          </div>
        </div>
      </div>
      <div v-if="files.length" class="text-xs text-gray-500">Nhấn "Tải lên" để bắt đầu tải {{ files.length }} ảnh.</div>

      <!-- Actions (đặt trong body vì woot-modal không có slot footer) -->
      <div class="flex justify-end gap-2 pt-3 border-t border-gray-200">
        <woot-button variant="clear" :disabled="isSubmitting" @click="handleClose">{{ $t('GENERAL.CANCEL') }}</woot-button>
        <woot-button :disabled="!files.length || isSubmitting" :is-loading="isSubmitting" @click="upload">{{ $t('GENERAL.UPLOAD') }}{{ files.length ? ` (${files.length})` : '' }}</woot-button>
      </div>
    </div>
  </woot-modal>
  
  <!-- Inline per-file edit modal -->
  <div v-if="showEdit" class="modal-backdrop" @click.self="closeEdit">
    <div class="modal-card p-4">
      <div class="text-sm font-medium text-gray-900 mb-3">{{ $t('IMAGE_LIBRARY.EDIT_ITEM') }}</div>
      <div class="space-y-3">
        <woot-input v-model="editForm.title" :label="$t('IMAGE_LIBRARY.FIELDS.TITLE')" />
        <div>
          <label class="block text-xs font-medium text-gray-700 mb-1">{{ $t('IMAGE_LIBRARY.FIELDS.DESCRIPTION') }}</label>
          <textarea v-model="editForm.description" rows="5" maxlength="1000" class="w-full border border-gray-300 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
          <div class="text-right text-xs text-gray-500 mt-1">{{ (editForm.description || '').length }}/1000</div>
        </div>
      </div>
      <div class="mt-4 flex justify-end gap-2">
        <woot-button variant="clear" @click="closeEdit">{{ $t('GENERAL.CANCEL') }}</woot-button>
        <woot-button @click="saveEdit">{{ $t('GENERAL.SAVE') }}</woot-button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ItemUploadModal',
  props: {
    show: { type: Boolean, default: false },
    accountId: { type: [String, Number], required: true },
    libraryId: { type: [String, Number], required: true },
  },
  emits: ['close', 'uploaded'],
  
  data() {
    return {
      files: [], // [{ file, url, title?, description? }]
      isSubmitting: false,
      progress: 0,
      description: '',
      showEdit: false,
      editingIndex: -1,
      editForm: { title: '', description: '' },
    }
  },
  computed: {
    uploadLabel() {
      if (this.isSubmitting) return `Đang tải... ${this.progress}%`
      return this.$t('GENERAL.UPLOAD') + (this.files.length ? ` (${this.files.length})` : '')
    }
  },
  methods: {
    handleClose() { this.revokeAll(); this.$emit('close') },
    openPicker() { if (this.$refs.fileInput) { this.$refs.fileInput.click() } },
    onFiles(e) {
      const list = Array.from(e.target.files || [])
      this.appendFiles(list)
      e.target.value = ''
    },
    onDrop(e) {
      const list = Array.from(e.dataTransfer.files || [])
      this.appendFiles(list)
    },
    appendFiles(list) {
      const images = list.filter(f => f && f.type && f.type.startsWith('image/'))
      const nonImages = list.length - images.length
      if (nonImages > 0) {
        this.$store.dispatch('setBanner', { type: 'warning', message: this.$t('GENERAL.INVALID_FILE_TYPE') || 'Tệp không hợp lệ' })
      }
      images.forEach(f => {
        try {
          const url = URL.createObjectURL(f)
          this.files.push({ file: f, url, title: f.name, description: this.description || '' })
        } catch (e) {
          const reader = new FileReader()
          reader.onload = ev => {
            this.files.push({ file: f, url: ev.target.result, title: f.name, description: this.description || '' })
          }
          reader.readAsDataURL(f)
        }
      })
    },
    removeAt(idx) {
      const f = this.files[idx]
      if (f?.url) URL.revokeObjectURL(f.url)
      this.files.splice(idx, 1)
    },
    revokeAll() {
      this.files.forEach(f => f?.url && URL.revokeObjectURL(f.url))
      this.files = []
    },
    async upload() {
      try {
        this.isSubmitting = true
        this.progress = 0
        const form = new FormData()
        this.files.forEach(f => form.append('files[]', f.file))
        // per-file metadata
        this.files.forEach(f => form.append('titles[]', f.title || f.file.name))
        this.files.forEach(f => form.append('descriptions[]', (f.description || '').toString()))
        if (this.description) form.append('description', this.description)
        await window.axios.post(
          `/api/v1/accounts/${this.accountId}/image_libraries/${this.libraryId}/image_items/bulk_upload`,
          form,
          {
            onUploadProgress: (e) => {
              if (e && e.total) {
                this.progress = Math.round((e.loaded * 100) / e.total)
              }
            }
          }
        )
        this.revokeAll()
        this.$emit('uploaded')
      } finally {
        this.isSubmitting = false
        this.progress = 0
      }
    },
    openEdit(idx) {
      const f = this.files[idx]
      if (!f) return
      this.editingIndex = idx
      this.editForm.title = f.title || f.file.name
      this.editForm.description = f.description || ''
      this.showEdit = true
    },
    closeEdit() {
      this.showEdit = false
      this.editingIndex = -1
      this.editForm = { title: '', description: '' }
    },
    saveEdit() {
      if (this.editingIndex < 0) return
      const f = this.files[this.editingIndex]
      if (!f) return
      f.title = this.editForm.title || f.file.name
      f.description = this.editForm.description || ''
      this.closeEdit()
    },
    onImgError(idx) {
      const f = this.files[idx]
      if (!f) return
      f.url = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw=='
    },
    humanSize(bytes) {
      if (!bytes) return ''
      const i = Math.floor(Math.log(bytes) / Math.log(1024))
      const sizes = ['B', 'KB', 'MB', 'GB']
      return `${(bytes / (1024 ** i)).toFixed(2)} ${sizes[i]}`
    }
  }
}
</script>

<style scoped>
.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
}
.modal-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  box-shadow: 0 10px 25px rgba(0,0,0,0.1);
  width: 100%;
  max-width: 520px;
}
</style>

